import { useState } from 'react'
import { Check } from 'lucide-react'
import { Modal } from './Modal'
import { principalClassLabel } from '../theme/vocabulary'
import type { PrincipalClass, TokenIssuanceResponse } from '../api/types'
import { Notice } from './primitives/Notice'

// Surfaces the raw license token exactly once (issuance / rotation). The token
// is never persisted or re-fetchable — it exists only in this component's props
// for the life of the modal (ATC-ADR-011). Copy-to-clipboard + an explicit
// "won't be shown again" warning, plus a "where to put this" checklist.
//
// variant distinguishes a fresh issuance ('issued') from a rotation ('rotated');
// a rotation additionally invalidated a prior token, which the operator must
// re-propagate (ATC-060).
//
// ATC-131 adds `principalClass`, and it is a CORRECTNESS fix rather than a
// nicety: this dialog showed the DEVICE consumer checklist — "MSI install
// arguments" — for every reveal. An `atc_fd_` token must NEVER be baked into
// an MSI install argument; the whole point of ATC-ADR-016's class is that it
// lives on the Manager Function App and structurally not on any device. A
// checklist telling an operator to do the opposite is worse than no checklist.
type RevealVariant = 'issued' | 'rotated'

export function TokenRevealModal({
  result,
  onClose,
  variant = 'issued',
  principalClass = 'device',
  readBackWarning,
  customerName,
}: {
  result: TokenIssuanceResponse
  onClose: () => void
  variant?: RevealVariant
  /** Defaults to `device`, which is what every pre-ATC-131 caller meant. */
  principalClass?: PrincipalClass
  /**
   * ATC-139. A read-back discrepancy, rendered HERE rather than as a page
   * notice, because this modal is the one screen an operator is certain to be
   * looking at and the token behind it is already minted. A warning behind a
   * modal is a warning nobody reads.
   */
  readBackWarning?: string
  /**
   * ATC-141. The CUSTOMER's name, when the caller knows it — the wizard does,
   * because it just created the account. Omitted on a bare issuance, where the
   * environment is not yet mapped and there is genuinely no customer to name.
   */
  customerName?: string
}) {
  const [copied, setCopied] = useState(false)
  const rotated = variant === 'rotated'
  const isFlightDeck = principalClass === 'flightdeck'

  async function copy() {
    try {
      await navigator.clipboard.writeText(result.licenseToken)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  // ATC-111: `deliberate`. Not checkbox-gated, but this is the dialog where an
  // unaimed dismissal costs the most — the raw token is already minted, is
  // never re-fetchable, and recovering it means rotating again, which breaks
  // Flight Deck Manager's RUBIX_LICENSE_TOKEN a second time. The copy below
  // already says so ("Closing this dialog discards the token permanently").
  // The pointer form of the hazard is real here too: selecting the token text
  // and releasing past the dialog edge lands the click on the backdrop. The
  // explicit Done / Close-without-copying button and the ✕ still close it.
  return (
    <Modal
      title={rotated ? 'New token — the previous one is now invalid' : 'License token — shown once'}
      onClose={onClose}
      dismissal="deliberate"
    >
      <div className="notice notice-warn">
        This is the only time this token is shown. Copy it now and store it securely — it
        cannot be retrieved again.
      </div>

      {rotated && (
        <Notice tone="error">
          The previous token for this environment has been <strong>permanently invalidated</strong>.
          Anything still using it starts returning 403 within ~60 seconds.
        </Notice>
      )}

      {readBackWarning && <Notice tone="error">{readBackWarning}</Notice>}

      <dl className="kv">
        {/* ATC-141. These two rows were LABELLED WRONG, and ATC-140 made it
            visible: `customerName` holds the generated ENVIRONMENT label, so
            this dialog read "Customer: vivid-windward" — naming an environment
            as the customer, on the one screen an operator is certain to read.
            The customer is a separate thing and is only known once the
            environment is mapped, so it is passed in when the caller has it and
            omitted when it does not, rather than inferred from a field that
            never meant it. */}
        {customerName && (
          <>
            <dt>Customer</dt>
            <dd>{customerName}</dd>
          </>
        )}
        <dt>Environment</dt>
        <dd>{result.customerName}</dd>
        <dt>Environment ID</dt>
        <dd className="cei">{result.customerEnvironmentId}</dd>
        <dt>Credential</dt>
        <dd>{principalClassLabel(principalClass)}</dd>
        <dt>Issued</dt>
        <dd>{result.issuedAt}</dd>
        {/* ATC-139. Sourced from the RESPONSE, never echoed from the request —
            the point is to show what was STORED. Hotfix 054 shipped a path
            that accepted an expiry and stored nothing, and this modal, the one
            screen guaranteed to be read, did not mention expiry at all. */}
        <dt>Expires</dt>
        <dd>
          {result.expiresAt === undefined
            ? 'not reported'
            : result.expiresAt === null
              ? 'never'
              : result.expiresAt}
        </dd>
      </dl>

      <label className="field-label">License token</label>
      <div className="token-row">
        <code className="token-value">{result.licenseToken}</code>
        {/* ATC-110: a Unicode check glyph stood in here; lucide, strokeWidth 1.5. */}
        <button className="copy-button" onClick={copy}>
          {copied ? (
            <>
              <Check size={16} strokeWidth={1.5} aria-hidden="true" />
              Copied
            </>
          ) : (
            'Copy'
          )}
        </button>
      </div>

      <p className="field-label" style={{ marginTop: '1rem' }}>
        {rotated ? 'Update these now' : 'Configure these consumers'}
      </p>
      {isFlightDeck ? (
        <>
          <ul className="checklist">
            <li>
              the Flight Deck Manager’s <code>RUBIX_FD_LICENSE_TOKEN</code> app setting
              <span className="muted"> (a SECOND app setting, beside RUBIX_LICENSE_TOKEN)</span>
            </li>
          </ul>
          <div className="notice notice-warn">
            <strong>Never put this token on a device.</strong> It is the{' '}
            <code>atc_fd_</code> class (<code>ATC-ADR-016</code>): it gates the ppkg build
            endpoint, and it is meaningful only because devices structurally do not hold it. It
            must not go into MSI install arguments.
          </div>
        </>
      ) : (
        <ul className="checklist">
          <li>
            the Flight Deck Manager’s <code>RUBIX_LICENSE_TOKEN</code> app setting
            {rotated ? ', then re-run the Flight Deck staging smoke' : ' for this environment'}
          </li>
          <li>
            {rotated
              ? 'any devices with the old token baked into their MSI install arguments (re-provision)'
              : 'device MSI install arguments at onboarding'}
          </li>
        </ul>
      )}

      <p className="muted">Closing this dialog discards the token permanently — copy it first.</p>

      <div className="modal-actions">
        <button className="btn-primary" onClick={onClose}>
          {copied ? 'Done' : 'Close without copying'}
        </button>
      </div>
    </Modal>
  )
}
